import {
  Menu,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Tooltip,
} from '@fluentui/react-components';
import { ChevronDownRegular, type FluentIcon } from '@fluentui/react-icons';
import { useState } from 'react';
import { pagePath } from '#src/app/navigation/module';
import type { VisibleModule } from '#src/app/navigation/navigation';
import { useSidebarStyles } from '#src/app/shell/sidebar.styles';

export interface NavButtonProps {
  readonly icon?: FluentIcon;
  readonly label: string;
  readonly selected: boolean;
  readonly sub?: boolean;
  /** Barra recolhida: só o ícone, com o nome numa dica. */
  readonly collapsed?: boolean;
  onClick(): void;
}

export function NavButton({
  icon: Icon,
  label,
  selected,
  sub = false,
  collapsed = false,
  onClick,
}: NavButtonProps) {
  const styles = useSidebarStyles();
  const button = (
    <button
      type="button"
      className={mergeClasses(
        styles.item,
        sub && styles.subItem,
        collapsed && styles.itemCollapsed,
        selected && styles.selected,
      )}
      aria-current={selected ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
      onClick={onClick}
    >
      {Icon && (
        <span
          className={mergeClasses(styles.icon, selected && styles.iconSelected)}
          aria-hidden
        >
          <Icon />
        </span>
      )}
      {!collapsed && <span className={styles.label}>{label}</span>}
    </button>
  );
  if (!collapsed) {
    return button;
  }
  return (
    <Tooltip content={label} relationship="label" positioning="after">
      {button}
    </Tooltip>
  );
}

// Medidas de sidebar.styles.ts, para o mínimo de uma pasta aberta.
const HEADER_HEIGHT = 40;
const SUB_ITEM_HEIGHT = 36;
const SUB_ITEM_GAP = 2;
const LIST_MARGINS = 6;

interface ModuleGroupProps extends VisibleModule {
  /** Caminho da página selecionada. */
  readonly selected: string;
  /** O endereço atual é deste módulo. */
  readonly active: boolean;
  readonly collapsed: boolean;
  onNavigate(path: string): void;
}

/** Barra recolhida: o ícone do módulo abre as páginas num menu ao lado. */
function CollapsedModule({
  module,
  pages,
  active,
  onNavigate,
}: ModuleGroupProps) {
  const styles = useSidebarStyles();
  const Icon = module.icon;
  return (
    <Menu positioning="after-top">
      <Tooltip content={module.title} relationship="label" positioning="after">
        <MenuTrigger disableButtonEnhancement>
          <button
            type="button"
            className={mergeClasses(
              styles.item,
              styles.itemCollapsed,
              active && styles.selected,
            )}
          >
            <span
              className={mergeClasses(
                styles.icon,
                active && styles.iconSelected,
              )}
              aria-hidden
            >
              <Icon />
            </span>
          </button>
        </MenuTrigger>
      </Tooltip>
      <MenuPopover>
        <MenuList>
          <MenuGroup>
            <MenuGroupHeader>{module.title}</MenuGroupHeader>
            {pages.map((page) => (
              <MenuItem
                key={page.path}
                onClick={() => onNavigate(pagePath(module, page))}
              >
                {page.title}
              </MenuItem>
            ))}
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

/** Pasta do módulo: cabeçalho que abre e fecha, e as páginas abaixo. */
function ModuleFolder({
  module,
  pages,
  selected,
  active,
  onNavigate,
}: ModuleGroupProps) {
  const styles = useSidebarStyles();
  const [open, setOpen] = useState(true);
  const Icon = module.icon;
  const listId = `nav-${module.path}`;
  // Com pouca altura a lista encolhe e rola, mas mantém até duas páginas.
  const visible = Math.min(pages.length, 2);
  const listMin = visible * SUB_ITEM_HEIGHT + (visible - 1) * SUB_ITEM_GAP;

  return (
    <div
      className={styles.group}
      style={
        open ? { minHeight: HEADER_HEIGHT + LIST_MARGINS + listMin } : undefined
      }
    >
      <button
        type="button"
        className={mergeClasses(
          styles.item,
          styles.groupHeader,
          active && styles.groupActive,
        )}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className={mergeClasses(styles.icon, active && styles.iconSelected)}
          aria-hidden
        >
          <Icon />
        </span>
        <span className={styles.label}>{module.title}</span>
        <ChevronDownRegular
          className={mergeClasses(
            styles.chevron,
            !open && styles.chevronClosed,
          )}
        />
      </button>
      {open && (
        <ul
          id={listId}
          className={styles.subList}
          style={{ minHeight: listMin }}
        >
          {pages.map((page) => {
            const path = pagePath(module, page);
            return (
              <li key={page.path} className={styles.subListItem}>
                <NavButton
                  label={page.title}
                  selected={selected === path}
                  sub
                  onClick={() => onNavigate(path)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Módulo principal é sempre uma pasta expansível, mesmo com uma página só
 * visível. Utilitários do rodapé com uma página viram item direto. Com a
 * barra recolhida, o ícone abre as páginas num menu ao lado.
 */
export function ModuleGroup(props: ModuleGroupProps) {
  const { module, pages, selected, collapsed, onNavigate } = props;
  const [single] = pages;

  if (single === undefined) {
    return null;
  }
  if (pages.length === 1 && module.placement === 'footer') {
    const path = pagePath(module, single);
    return (
      <NavButton
        icon={module.icon}
        label={module.title}
        selected={selected === path}
        collapsed={collapsed}
        onClick={() => onNavigate(path)}
      />
    );
  }
  return collapsed ? (
    <CollapsedModule {...props} />
  ) : (
    <ModuleFolder {...props} />
  );
}
